import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  TextField,
  MenuItem,
  Skeleton,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Tooltip,
  IconButton,
  Divider,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getPipelineConfig, updatePipelineConfig } from '../api';
import { useAuth } from '../context/AuthContext';
import { usePipeline } from '../context/PipelineContext';
import { styled } from '@mui/material/styles';
import {
  HourglassEmpty as PendingIcon,
  CheckCircleOutline as ApprovedIcon,
  Description as InvoicedIcon,
  PlaylistAddCheck as PayrollReadyIcon,
  Paid as PaidIcon,
} from '@mui/icons-material';
import { getPipelineSummary, getPipelineEntries, getCompanies, getEmployees } from '../api';
import StatusChip, { getStatusTone, TONE_PALETTE_KEY } from './StatusChip';

// Stage color = the StatusChip tone for that status, as a theme palette path
// (resolved by sx, so light/dark both work). Neutral → text.secondary.
const stageColor = (key) => {
  const paletteKey = TONE_PALETTE_KEY[getStatusTone(key)];
  return paletteKey ? `${paletteKey}.main` : 'text.secondary';
};

const ALL_PIPELINE_STAGES = [
  { key: 'pending', label: 'Pending', icon: <PendingIcon />, color: stageColor('pending'), configurable: false, description: 'Hours submitted but not yet approved. Always enabled.' },
  { key: 'approved', label: 'Approved', icon: <ApprovedIcon />, color: stageColor('approved'), configurable: true, description: 'Requires a manager to approve time entries before they can move forward.' },
  { key: 'invoiced', label: 'Invoiced', icon: <InvoicedIcon />, color: stageColor('invoiced'), configurable: true, description: 'Requires creating a client invoice before paying employees for these hours.' },
  { key: 'invoice_paid', label: 'Invoice Paid', icon: <InvoicedIcon />, color: stageColor('invoice_paid'), configurable: true, description: 'Requires the client invoice to be marked paid before employee payroll runs.' },
  { key: 'payroll_ready', label: 'Payroll Ready', icon: <PayrollReadyIcon />, color: stageColor('payroll_ready'), configurable: true, description: 'Explicit "ready for payroll" gate. Disable for tenants that pay immediately after approval/invoicing.' },
  { key: 'paid', label: 'Paid', icon: <PaidIcon />, color: stageColor('paid'), configurable: false, description: 'Employee has been paid. Always enabled.' },
];

const PRESET_LABELS = {
  full: 'Full Pipeline (default)',
  no_invoice: 'No Invoicing (approval + payroll only)',
  no_approval: 'No Approval (auto-approve, invoice and pay)',
  direct_pay: 'Direct Pay (pending → paid, no gates)',
};

const StageConnector = styled(StepConnector)(({ theme }) => ({
  '& .MuiStepConnector-line': {
    borderTopWidth: 3,
    borderColor: theme.palette.divider,
  },
}));

const formatCurrency = (amount) => {
  // null/undefined = pay data withheld server-side (caller lacks
  // can_view_pay_rates) — show a mask, not a misleading $0.00
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
};

const formatHours = (hours) => {
  return parseFloat(hours || 0).toFixed(1);
};

export default function PaymentPipeline() {
  const { hasPermission } = useAuth();
  const { refresh: refreshPipelineStatus } = usePipeline();
  const canConfigure = hasPermission('can_configure_pipeline');

  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [selectedStage, setSelectedStage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [error, setError] = useState('');
  const [companies, setCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [pipelineConfig, setPipelineConfig] = useState(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [configDraft, setConfigDraft] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configError, setConfigError] = useState('');

  const visibleStages = useMemo(() => {
    if (!pipelineConfig) return ALL_PIPELINE_STAGES;
    return ALL_PIPELINE_STAGES.filter((s) => {
      if (!s.configurable) return true;
      return pipelineConfig.stages?.[s.key]?.enabled !== false;
    });
  }, [pipelineConfig]);

  // Filters
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    company_id: '',
    employee_id: '',
  });

  const loadSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.company_id) params.company_id = filters.company_id;
      if (filters.employee_id) params.employee_id = filters.employee_id;
      const res = await getPipelineSummary(params);
      setSummary(res.data);
    } catch (err) {
      setError('Failed to load pipeline summary');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadEntries = useCallback(async (status) => {
    try {
      setEntriesLoading(true);
      const params = { status };
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.company_id) params.company_id = filters.company_id;
      if (filters.employee_id) params.employee_id = filters.employee_id;
      const res = await getPipelineEntries(params);
      setEntries(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setEntriesLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadSummary();
    Promise.all([getCompanies(), getEmployees()])
      .then(([compRes, empRes]) => {
        setCompanies(compRes.data || []);
        setEmployees(empRes.data || []);
      })
      .catch(() => {});
    if (canConfigure) {
      getPipelineConfig()
        .then((res) => setPipelineConfig(res.data.config))
        .catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadSummary();
    if (selectedStage) loadEntries(selectedStage);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStageClick = (stageKey) => {
    if (selectedStage === stageKey) {
      setSelectedStage(null);
      setEntries([]);
    } else {
      setSelectedStage(stageKey);
      loadEntries(stageKey);
    }
  };

  const handleFilterChange = (field) => (e) => {
    setFilters(prev => ({ ...prev, [field]: e.target.value }));
  };

  const getStatusChip = (status) => {
    const stage = ALL_PIPELINE_STAGES.find(s => s.key === status);
    return <StatusChip status={status} label={stage?.label} title={stage?.description} />;
  };

  // Grand totals — amount goes null (masked) when any stage's pay data is
  // withheld server-side, instead of summing to a misleading $0.00
  const grandTotal = summary ? Object.values(summary).reduce((acc, s) => ({
    count: acc.count + (s.total?.count || 0),
    amount: (acc.amount === null || (s.total && s.total.count > 0 && s.total.amount == null))
      ? null
      : acc.amount + (s.total?.amount || 0),
    hours: acc.hours + (s.total?.hours || 0),
  }), { count: 0, amount: 0, hours: 0 }) : null;

  const openConfigDialog = async () => {
    setConfigError('');
    try {
      const res = await getPipelineConfig();
      setConfigDraft(JSON.parse(JSON.stringify(res.data.config)));
      setConfigDialogOpen(true);
    } catch (err) {
      setConfigError(err.response?.data?.error || 'Failed to load pipeline config');
      setConfigDialogOpen(true);
    }
  };

  const applyPreset = (presetKey) => {
    const PRESETS = {
      full:        { approved: true,  invoiced: true,  invoice_paid: true,  payroll_ready: true  },
      no_invoice:  { approved: true,  invoiced: false, invoice_paid: false, payroll_ready: true  },
      no_approval: { approved: false, invoiced: true,  invoice_paid: true,  payroll_ready: true  },
      direct_pay:  { approved: false, invoiced: false, invoice_paid: false, payroll_ready: false },
    };
    const preset = PRESETS[presetKey];
    if (!preset || !configDraft) return;
    const next = { stages: { ...configDraft.stages } };
    for (const k of Object.keys(preset)) {
      next.stages[k] = { ...(next.stages[k] || {}), enabled: preset[k] };
    }
    setConfigDraft(next);
  };

  const toggleStage = (key) => {
    if (!configDraft) return;
    const current = configDraft.stages?.[key]?.enabled !== false;
    const next = { stages: { ...configDraft.stages } };
    next.stages[key] = { ...(next.stages[key] || {}), enabled: !current };
    if (key === 'invoiced' && current) {
      next.stages.invoice_paid = { ...(next.stages.invoice_paid || {}), enabled: false };
    }
    setConfigDraft(next);
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    setConfigError('');
    try {
      // Preserve the current pipeline_enabled state when saving stage config from this page
      // (the toggle for pipeline_enabled lives in CompanySettings, not here)
      const res = await updatePipelineConfig({ ...configDraft, pipeline_enabled: true });
      setPipelineConfig(res.data.config);
      setConfigDialogOpen(false);
      // Refresh global pipeline context so nav items, buttons, etc. update everywhere
      if (refreshPipelineStatus) await refreshPipelineStatus();
    } catch (err) {
      setConfigError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSavingConfig(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Payment Pipeline
        </Typography>
        {canConfigure && (
          <Tooltip title="Configure which stages of the payment pipeline this tenant uses. Disable invoicing or approval if your company pays employees directly.">
            <Button
              variant="outlined"
              size="small"
              startIcon={<SettingsIcon />}
              onClick={openConfigDialog}
            >
              Configure Pipeline
            </Button>
          </Tooltip>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              label="Start Date"
              type="date"
              size="small"
              fullWidth
              value={filters.start_date}
              onChange={handleFilterChange('start_date')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="End Date"
              type="date"
              size="small"
              fullWidth
              value={filters.end_date}
              onChange={handleFilterChange('end_date')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Client"
              select
              size="small"
              fullWidth
              value={filters.company_id}
              onChange={handleFilterChange('company_id')}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">All Clients</MenuItem>
              {companies.map(c => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Employee"
              select
              size="small"
              fullWidth
              value={filters.employee_id}
              onChange={handleFilterChange('employee_id')}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">All Employees</MenuItem>
              {employees.map(e => (
                <MenuItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Pipeline Flow Visualization */}
      {loading ? (
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} variant="rectangular" height={120} sx={{ flex: 1, borderRadius: 1 }} />
          ))}
        </Box>
      ) : summary && (
        <>
          {/* Stepper visualization */}
          <Stepper
            alternativeLabel
            activeStep={-1}
            connector={<StageConnector />}
            sx={{ mb: 3 }}
          >
            {visibleStages.map((stage) => {
              return (
                <Step key={stage.key} completed={false}>
                  <StepLabel
                    StepIconComponent={() => (
                      <Box sx={{
                        color: stage.color,
                        display: 'flex',
                        alignItems: 'center',
                        '& svg': { fontSize: 28 }
                      }}>
                        {stage.icon}
                      </Box>
                    )}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {stage.label}
                    </Typography>
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>

          {/* Stage Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {visibleStages.map((stage) => {
              const data = summary[stage.key];
              const isSelected = selectedStage === stage.key;
              return (
                <Grid item xs={12} sm={6} md key={stage.key}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      borderWidth: 2,
                      borderStyle: 'solid',
                      borderColor: isSelected ? stage.color : 'transparent',
                      transition: 'all 0.2s',
                      '&:hover': { boxShadow: 4 },
                    }}
                    onClick={() => handleStageClick(stage.key)}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="h4" sx={{ color: stage.color, fontWeight: 700 }}>
                        {data?.total?.count || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        entries
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {formatCurrency(data?.total?.amount)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatHours(data?.total?.hours)} hrs
                      </Typography>
                      {data?.billable?.count > 0 && data?.non_billable?.count > 0 && (
                        <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <Chip label={`${data.billable.count} billable`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                          <Chip label={`${data.non_billable.count} internal`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Grand totals */}
          {grandTotal && (
            <Paper sx={{ p: 1.5, mb: 3, bgcolor: 'background.surface2', textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" component="span">
                Total: <strong>{grandTotal.count}</strong> entries | <strong>{formatCurrency(grandTotal.amount)}</strong> | <strong>{formatHours(grandTotal.hours)}</strong> hrs
              </Typography>
            </Paper>
          )}
        </>
      )}

      {/* Drill-down Table */}
      {selectedStage && (
        <Paper sx={{ mt: 1 }}>
          <Typography variant="h6" sx={{ p: 2, pb: 1 }}>
            {ALL_PIPELINE_STAGES.find(s => s.key === selectedStage)?.label} Entries
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Employee</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell align="right">Hours</TableCell>
                  <TableCell align="right">Rate</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entriesLoading ? (
                  [1, 2, 3].map(i => (
                    <TableRow key={i}>
                      {[1, 2, 3, 4, 5, 6, 7].map(j => (
                        <TableCell key={j}><Skeleton /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No entries in this stage
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map(entry => (
                    <TableRow key={entry.id} hover>
                      <TableCell>{new Date(entry.entry_date).toLocaleDateString()}</TableCell>
                      <TableCell>{entry.first_name} {entry.last_name}</TableCell>
                      <TableCell>{entry.company_name || <Typography variant="body2" color="text.secondary" component="span">Internal</Typography>}</TableCell>
                      <TableCell align="right">{formatHours(entry.hours_worked)}</TableCell>
                      <TableCell align="right">{formatCurrency(entry.hourly_rate)}</TableCell>
                      <TableCell align="right">{formatCurrency(entry.total_pay)}</TableCell>
                      <TableCell>{getStatusChip(entry.payment_status)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={configDialogOpen} onClose={() => setConfigDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Configure Payment Pipeline
          <Typography variant="caption" color="text.secondary" component="div">
            Choose which stages your company uses between Pending and Paid.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {configError && <Alert severity="error" sx={{ mb: 2 }}>{configError}</Alert>}
          {configDraft && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Presets</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {Object.keys(PRESET_LABELS).map((k) => (
                  <Button key={k} size="small" variant="outlined" onClick={() => applyPreset(k)}>
                    {PRESET_LABELS[k]}
                  </Button>
                ))}
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Stages</Typography>
              {ALL_PIPELINE_STAGES.filter(s => s.configurable).map((stage) => {
                const enabled = configDraft.stages?.[stage.key]?.enabled !== false;
                return (
                  <Box key={stage.key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ color: stage.color, display: 'flex' }}>{stage.icon}</Box>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>{stage.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{stage.description}</Typography>
                      </Box>
                      <Tooltip title={stage.description}>
                        <IconButton size="small"><InfoOutlinedIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </Box>
                    <FormControlLabel
                      control={<Switch checked={enabled} onChange={() => toggleStage(stage.key)} />}
                      label={enabled ? 'Enabled' : 'Skipped'}
                      labelPlacement="start"
                    />
                  </Box>
                );
              })}
              <Alert severity="info" sx={{ mt: 2 }}>
                Payroll automation always pulls from the last enabled stage before <strong>Paid</strong>.
                Disabling a stage causes entries to skip directly to the next enabled stage when advanced.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveConfig} disabled={savingConfig || !configDraft}>
            {savingConfig ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
